import { useState, useMemo, useDeferredValue, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../ui/table";
import { Search, Network, Server, Monitor } from "lucide-react";
import { Badge } from "../../ui/badge";
import { Device } from "../interfaces/EndpointInterfaces";
import { myLocalStorage } from "../../StorageHelper";
import { fetchAllComputers, fetchGatewayDevices } from "./Api/gatewaydevicesapi";
import { transformApiToDevice } from "../Endpoints/helper";
import { Skeleton } from "../interfaces/CommonInterfaces"; // ✅ imported Skeleton

interface GatewayDevicesTabProps {
    device: Device;
}

export default function GatewayDevicesTab({ device }: GatewayDevicesTabProps) {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const tenantName = myLocalStorage.getItem("latestTenant").tenantName || "";
    const [gatewayDevices, setGatewayDevices] = useState<Device[]>([]);
    const [loading, setLoading] = useState<boolean>(true); // ✅ loading state

    const itemsPerPage = 10;
    const deferredSearchTerm = useDeferredValue(searchTerm);

    const getGateWaydevices = async () => {
        try {
            setLoading(true); // ✅ start loading
            const response = await fetchGatewayDevices(tenantName, device.id);
            const devices = transformApiToDevice(response);
            if (response) {

                setGatewayDevices(devices);
            }
        } catch (error) {
            console.error("Error fetching gateway devices:", error);
        } finally {
            setLoading(false); // ✅ stop loading
        }
    };

    useEffect(() => {
        getGateWaydevices();
    }, [tenantName]);

    const filteredDevices = useMemo(() => {
        const term = deferredSearchTerm.toLowerCase();
        return gatewayDevices.filter((gatewayDevice) =>
            gatewayDevice.dNSHostName?.toLowerCase().includes(term) ||
            gatewayDevice.IPV4Address?.includes(deferredSearchTerm) ||
            gatewayDevice.protocol?.toLowerCase().includes(term) ||
            gatewayDevice.OperatingSystem?.toLowerCase().includes(term)
        );
    }, [deferredSearchTerm, gatewayDevices]);

    const totalPages = Math.ceil(filteredDevices.length / itemsPerPage);
    const paginatedDevices = filteredDevices.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [deferredSearchTerm]);

    const getOSIcon = (os: string) => {
        if (os.toLowerCase().includes("linux")) {
            return <Server className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
        }
        return <Monitor className="w-4 h-4 text-muted-foreground" />;
    };

    return (
        <div className="space-y-6 p-6" data-testid="gateway-devices-tab">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-2xl font-semibold text-foreground" data-testid="title-gateway-devices">
                    Gateway Devices
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                    Devices connecting through {device.name} gateway
                </p>
            </div>

            {/* Search */}
            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search gateway devices by name, IP address, protocol, or OS..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="input-search-gateway-devices"
                            />
                        </div>
                        {loading && <Skeleton className="w-12 h-4 ml-auto" />} {/* ✅ Skeleton */}
                    </div>
                </CardContent>
            </Card>

            {/* Gateway Devices Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Network className="w-5 h-5" />
                        Gateway Devices ({filteredDevices.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        // ✅ Skeleton loading rows
                        <div className="p-6 space-y-4">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="flex items-center justify-between">
                                    <Skeleton className="w-8 h-4" />
                                    <Skeleton className="w-32 h-4" />
                                    <Skeleton className="w-24 h-4" />
                                    <Skeleton className="w-20 h-4" />
                                    <Skeleton className="w-28 h-4" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50">
                                        <TableHead className="pl-6 font-semibold">Sl No</TableHead>
                                        <TableHead className="font-semibold">Device Name</TableHead>
                                        <TableHead className="font-semibold">IP Address</TableHead>
                                        <TableHead className="font-semibold">Protocol</TableHead>
                                        <TableHead className="font-semibold">Operating System</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paginatedDevices.map((gatewayDevice, index) => (
                                        <TableRow key={gatewayDevice.id}>
                                            <TableCell className="pl-6 font-medium">
                                                {(currentPage - 1) * itemsPerPage + index + 1}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                                        <Server className="w-4 h-4 text-primary" />
                                                    </div>
                                                    <div className="font-medium">
                                                        {gatewayDevice.dNSHostName}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="font-mono text-sm">
                                                    {gatewayDevice.IPV4Address}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="font-mono text-xs">
                                                    {gatewayDevice.protocol?.toUpperCase()}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {getOSIcon(gatewayDevice.OperatingSystem || "")}
                                                    <span className="font-medium">
                                                        {gatewayDevice.OperatingSystem}
                                                    </span>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}

                    {!loading && paginatedDevices.length === 0 && (
                        <div className="p-8 text-center">
                            <Network className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-foreground mb-2">
                                No Gateway Devices Found
                            </h3>
                            <p className="text-muted-foreground">
                                {searchTerm
                                    ? "No devices match your search criteria."
                                    : "No devices are currently connected through this gateway."}
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {!loading && totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                        {Math.min(currentPage * itemsPerPage, filteredDevices.length)} of{" "}
                        {filteredDevices.length} gateway devices
                    </div>
                    <div className="flex items-center space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </Button>
                        <span className="text-sm font-medium">
                            Page {currentPage} of {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
